// src/tabs/FilesTab.js

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, message, Modal, Upload, Form } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import GenericTable from '../components/GenericTable';
import ReactMarkdown from 'react-markdown';
import {
  fetchKbFilesRequest,
  uploadFilesRequest,
  deleteKbFileRequest,
  fetchKbFileContentRequest,
  clearFileContent,
  downloadFileRequest 
} from '../redux-saga/actions/knowledgeBaseActions';
import { useParams } from 'react-router-dom';

const FilesTab = () => {
  const dispatch = useDispatch();
  const { kbId } = useParams();
  const files = useSelector((state) => state.knowledgeBase.files[kbId] || []);
  const fileContent = useSelector((state) => state.knowledgeBase.fileContent);
  // const loading = useSelector((state) => state.knowledgeBase.loading);
  const error = useSelector((state) => state.knowledgeBase.error);

  const [fileModalVisible, setFileModalVisible] = useState(false);
  const [fileViewModalVisible, setFileViewModalVisible] = useState(false);
  const [uploadingFile, setUploadingFile] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    if (kbId) {
      dispatch(fetchKbFilesRequest(kbId));
    }
  }, [dispatch, kbId]);

  const handleViewFile = (fileId) => {
    dispatch(fetchKbFileContentRequest(fileId));
    setFileViewModalVisible(true);
  };

  const handleDownload = (fileId) => {
    dispatch(downloadFileRequest(fileId));
  };

  const handleDeleteFile = (fileId) => {
    dispatch(deleteKbFileRequest(fileId, kbId));
  };

  const handleAddFile = () => {
    if (uploadingFile.length === 0) {
      message.error('Please select files to upload.');
      return;
    }

    const formData = new FormData();
    uploadingFile.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('knowledgeId', kbId);

    dispatch(uploadFilesRequest(formData));
    setFileModalVisible(false);
    form.resetFields();
    setUploadingFile([]);
  };

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  // Configure columns for GenericTable
  const columnsConfig = [
    { title: 'Name', dataIndex: 'name', sortable: true, filterable: true },
    { title: 'Size', dataIndex: 'size', sortable: true },
    { title: 'Status', dataIndex: 'status', filterable: true },
    {
      title: 'Download',
      dataIndex: 'id',
      render: (text, record) => (
        record.status !== 'FAILED' ? (
          <Button type="link" onClick={() => handleDownload(text)}>Download</Button>
        ) : null
      ),
    },
  ];

  // Define actions and corresponding functions
  const fileActions = ['View', 'Delete'];
  const fileActionFunctions = [
    (record) => {
      if (record.status !== 'FAILED') {
        handleViewFile(record.id);
      }
    },
    (record) => handleDeleteFile(record.id),
  ];

  return (
    <div >
      <div style={{display:"flex",justifyContent:"end"}}>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setFileModalVisible(true)}>
        Add New File
      </Button>
      </div>
     

      <GenericTable
        data={files}
        columnsConfig={columnsConfig}
        searchKeys={['name', 'status']}
        actions={fileActions}
        actionFunctions={fileActionFunctions}
        defaultPageSize={10}
        rowKey="id"
      />

      <Modal
        title="Add New File"
        visible={fileModalVisible}
        onOk={handleAddFile}
        onCancel={() => {
          setFileModalVisible(false);
          setUploadingFile([]);
          form.resetFields();
        }}
        okText="Submit"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="fileUpload"
            label="Upload Files"
            rules={[{ required: true, message: 'Please select files to upload!' }]}
          >
            <Upload
              multiple
              beforeUpload={(file) => {
                setUploadingFile((prev) => [...prev, file]);
                return false;
              }}
              accept=".md,.txt"
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="File Content"
        visible={fileViewModalVisible}
        onCancel={() => {
          setFileViewModalVisible(false);
          dispatch(clearFileContent());
        }}
        footer={null}
        width={800}
      >
        {fileContent ? (
          <div style={{ maxHeight: '60vh', overflowY: 'auto', color: 'black' }}>
            <ReactMarkdown>{fileContent}</ReactMarkdown>
          </div>
        ) : (
          <p>Loading...</p>
        )}
      </Modal>
    </div>
  );
};

export default FilesTab;
